import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateIssueUpdateMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

export type UpdateIssueUpdateTRPCInput = {
  Id: string;
  ParentIssueId: string;
  Title: string;
  Description: string;
  CustomAttributeData?: CustomAttributeData | null;
  OriginalTimestamp: string;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (): UpdateIssueUpdateMutation => {
  return {
    update_issue_update: {
      __typename: 'issue_update_mutation_response',
      affected_rows: 1,
    },
  };
};

export const useUpdateIssueUpdateTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.issueUpdate.update.mutationOptions({
      onSuccess: async () => {
        // Parity with the Apollo evictField('issue_update') cache eviction
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.issueUpdate.getById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.issueUpdate.register.queryKey(),
        });
      },
    }),
  });

  return {
    updateIssueUpdate: async (
      variables: UpdateIssueUpdateTRPCInput
    ): Promise<UpdateIssueUpdateMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
